/**
 * Hook for validating invite codes with debounce support
 */

import { useQuery } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { api } from "../lib/api";

interface InviteValidationResult {
  isValid: boolean;
  orgName: string | null;
  role: "owner" | "editor" | "viewer" | null;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Validate an invite code with debounce support
 * @param code - The invite code to validate
 * @param debounceMs - Debounce delay in milliseconds (default: 300)
 */
export function useInviteValidation(
  code: string,
  debounceMs: number = 300
): InviteValidationResult {
  const [debouncedCode, setDebouncedCode] = useState(code);

  // Debounce the code input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedCode(code);
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [code, debounceMs]);

  const query = useQuery({
    queryKey: ["invite-validation", debouncedCode],
    queryFn: async () => {
      const res = await api.invites[":code"].validate.$get({
        param: { code: debouncedCode },
      });
      if (!res.ok) {
        throw new Error("Failed to validate invite code");
      }
      return res.json();
    },
    enabled: debouncedCode.length > 0,
    staleTime: 30_000, // Cache for 30s
    retry: false, // Don't retry failed validation
  });

  // While debouncing (code !== debouncedCode), show loading
  const isDebouncing = code !== debouncedCode && code.length > 0;

  return {
    isValid: query.data?.valid ?? false,
    orgName: query.data?.valid ? (query.data.organizationName ?? null) : null,
    role: query.data?.valid
      ? ((query.data.role as "owner" | "editor" | "viewer") ?? null)
      : null,
    isLoading: isDebouncing || query.isLoading,
    error: query.error as Error | null,
  };
}
